import { LitElement } from 'lit';
import type { ConnectionHealthState } from './connection-health';
import type { LiveUpdateState } from './live-update-transport';

/**
 * What the connection-health monitor last decided, as the rest of the page reads it.
 */
export interface ConnectionHealthSnapshot {
  health: ConnectionHealthState;
  /** The live-update connection's own state, shown beside the server's on the overlay. */
  liveUpdates: LiveUpdateState;
  /** Whether the server answered the last reachability check; undefined before the first. */
  serverReachable: boolean | undefined;
  /** When the next reachability check runs while offline (epoch ms), for the overlay's countdown. */
  nextReachabilityCheckAt: number | undefined;
}

/**
 * Singleton notifier for network state and update activity.
 * Allows components to register for online/offline and update state changes.
 *
 * Since D-137 the state here is WRITTEN BY ONE THING, the connection-health monitor
 * (connection-health.ts, wired in app-index.ts), and `online` means that monitor's "not offline":
 * the server answers. It used to be the live-update connection's open/error, so a dropped stream
 * read as offline everywhere this is read. A dropped stream with the server answering is now
 * `health === 'reconnecting'` and `online` stays true.
 */
export class NetworkStateNotifier {
  private static _instance: { [key: string]: NetworkStateNotifier } = {};
  private listeners: LitElement[] = [];
  private callbacks: Set<() => void> = new Set();
  private _updatingCount: number = 0;
  /**
   * Starts `online` with live updates `disconnected`: nothing says the server is unreachable until
   * a check fails, and the monitor's first decision replaces it.
   */
  private _snapshot: ConnectionHealthSnapshot = {
    health: 'online',
    liveUpdates: 'disconnected',
    serverReachable: undefined,
    nextReachabilityCheckAt: undefined,
  };

  public static getInstance(instanceName?: string): NetworkStateNotifier {
    if (NetworkStateNotifier._instance[instanceName ?? 'default'] === undefined)
      NetworkStateNotifier._instance[instanceName ?? 'default'] =
        new NetworkStateNotifier();
    return this._instance[instanceName ?? 'default'];
  }
  /**
   * Register a LitElement as a listener for state updates.
   */
  public register(listener: LitElement): void {
    this.listeners = [
      ...this.listeners.filter((x) => x !== listener),
      listener,
    ];
  }
  /** Register a plain callback (non-LitElement) */
  public onChange(cb: () => void) {
    this.callbacks.add(cb);
    return () => this.callbacks.delete(cb);
  }
  /**
   * Unregister a LitElement from state updates.
   */
  public unRegister(listener: LitElement): void {
    this.listeners = this.listeners.filter((x) => x !== listener);
  }
  /**
   * Set the updating state. Increments or decrements the update count.
   */
  public setUpdating(value: boolean): void {
    if (value) this._updatingCount++;
    else this._updatingCount--;
    this.listeners.forEach((x) => x.requestUpdate());
  }
  /**
   * Whether the server answers: every state but `offline`. What a reader that decides "can I make
   * a request now" wants.
   */
  public get online(): boolean {
    return this._snapshot.health !== 'offline';
  }
  /** The monitor's whole decision. */
  public get health(): ConnectionHealthState {
    return this._snapshot.health;
  }
  /** The live-update connection's own state. */
  public get liveUpdates(): LiveUpdateState {
    return this._snapshot.liveUpdates;
  }
  public get snapshot(): ConnectionHealthSnapshot {
    return this._snapshot;
  }
  /**
   * Record the monitor's latest decision and notify listeners. Called by the connection-health
   * wiring in app-index.ts and by nothing else.
   */
  public setConnectionHealth(snapshot: ConnectionHealthSnapshot) {
    this._snapshot = snapshot;
    this.listeners.forEach((x) => x.requestUpdate());
    this.callbacks.forEach((cb) => {
      try {
        cb();
      } catch (e) {
        // swallow
      }
    });
  }
  /**
   * Returns true if any updates are pending.
   */
  public get updating(): boolean {
    return this._updatingCount > 0;
  }
}

/**
 * Helper to set the network updating state from anywhere in the app.
 */
export function setNetworkUpdatingState(value: boolean) {
  NetworkStateNotifier.getInstance().setUpdating(value);
}
